import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import OfflineNotification from '../OfflineNotification';
import logoImage from '../../assets/images/logo.png';

// Import icons
import {
  HomeIcon,
  UsersIcon,
  ClipboardDocumentCheckIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  XMarkIcon,
  Bars3Icon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  LanguageIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

// Main Layout Component
const Layout = () => {
  const { settings } = useSettings();
  const { user, logout, isLicenseExpiring, getRemainingDays } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // States
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Collapsed state for desktop sidebar
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false); // Separate state for mobile
  const [isRefreshing, setIsRefreshing] = useState(false); // Refresh state
  
  // Get RTL setting
  const isRTL = false; // Set to your language preference
  
  // Toggle functions
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  
  const toggleMobileSidebar = () => {
    setSidebarMobileOpen(!sidebarMobileOpen);
  };
  
  // Refresh function to reload current page data
  const handleRefresh = () => {
    setIsRefreshing(true);
    
    // Simply reload the current page
    setTimeout(() => {
      window.location.reload();
    }, 500); // Small delay for visual feedback
  };
  
  // Menu items
const menuItems = [
  { 
      name: 'Tableau de Bord',
      href: '/tableau-de-bord',
      icon: HomeIcon,
      current: location.pathname === '/tableau-de-bord',
    },
    {
      name: 'Clients',
      href: '/clients',
      icon: UsersIcon,
      current: location.pathname === '/clients',
    },
    {
      name: 'Réservations',
      href: '/reservations',
      icon: ClipboardDocumentCheckIcon,
      current: location.pathname === '/reservations',
    },
    {
      name: 'Forfaits',
      href: '/forfaits',
      icon: ClipboardDocumentCheckIcon,
      current: location.pathname === '/forfaits',
    },
    {
      name: 'Finances',
      href: '/finances',
      icon: BanknotesIcon,
      current: location.pathname === '/finances',
    },
    {
      name: 'Fournisseurs',
      href: '/fournisseurs',
      icon: BuildingOfficeIcon,
      current: location.pathname === '/fournisseurs',
    },
    {
      name: 'Calendrier',
      href: '/calendrier',
      icon: CalendarDaysIcon,
      current: location.pathname === '/calendrier',
    },
    {
      name: 'Documents',
      href: '/documents',
      icon: DocumentTextIcon,
      current: location.pathname === '/documents',
    },
    {
      name: 'Rapports',
      href: '/rapports',
      icon: ChartBarIcon,
      current: location.pathname === '/rapports',
    },
    {
      name: 'Paramètres',
      href: '/parametres',
      icon: Cog6ToothIcon,
      current: location.pathname === '/parametres',
    },
  ];

  // Function to close sidebar when clicking links on mobile
  const closeSidebar = () => {
    if (window.innerWidth < 1024) {
      setSidebarMobileOpen(false);
    }
  };

  // Get current page title
  const getCurrentPageTitle = () => {
    const currentItem = menuItems.find(item => item.href === location.pathname);
    return currentItem ? currentItem.name : settings.appName || 'TravelIT';
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="flex h-screen overflow-hidden">
        {/* Desktop Sidebar */}
        <aside
          className={`${isRTL ? 'order-2' : 'order-1'} hidden lg:flex flex-col bg-white/5 backdrop-blur-xl border-r border-white/10 fixed inset-y-0 z-30 transition-all duration-300 ease-in-out ${
            sidebarCollapsed ? 'w-16' : 'w-56'
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Logo section */}
            <div className="flex-shrink-0 pt-5 pb-4 overflow-hidden">
              <div className={`flex items-center px-4 ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`}>
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
                  <img 
                    src={logoImage} 
                    alt="TravelIT Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                {!sidebarCollapsed && (
                  <div className="ml-3">
                    <h1 className="text-lg font-semibold text-white">
                      {settings.appName || 'TravelIT'}
                    </h1>
                    <p className="text-xs text-white/60">Gestion d'agence de voyage</p>
                  </div>
                )}
              </div>
            </div>

            {/* Toggle button */}
            <div className="flex-shrink-0 px-4 pb-4">
              <button
                onClick={toggleSidebar}
                className="w-full flex items-center justify-center p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
              >
                <Bars3Icon className={`h-5 w-5 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-0' : 'rotate-180'}`} />
              </button>
            </div>

            {/* Navigation */}
            <nav className="mt-4 flex-1 px-2 overflow-y-auto scrollbar-hide">
              <div className="space-y-1">
                {menuItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(item.href);
                      closeSidebar();
                    }}
                    className={`group flex items-center px-2 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      item.current
                        ? 'bg-white/10 text-white'
                        : 'text-white/60 hover:bg-white/5 hover:text-white'
                    } ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`}
                    title={sidebarCollapsed ? item.name : ''}
                  >
                    <item.icon
                      className={`flex-shrink-0 h-6 w-6 ${sidebarCollapsed ? 'mr-0' : 'mr-3'}`}
                      aria-hidden="true"
                    />
                    {!sidebarCollapsed && (
                      <span className="transition-opacity duration-200">{item.name}</span>
                    )}
                  </a>
                ))}
              </div>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <div className={`flex-1 flex flex-col overflow-hidden ${isRTL ? 'order-1' : 'order-2'} ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-56'} transition-all duration-300`}>
          {/* Header */}
          <header className="h-16 bg-white/10 backdrop-blur-lg border-b border-white/20 flex items-center justify-between px-4 md:px-6 z-20">
            {/* Left side - Menu button and page title */}
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                className="lg:hidden text-white/80 hover:text-white mr-4"
                onClick={toggleMobileSidebar}
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              
              {/* Desktop sidebar toggle button */}
              <button
                className="hidden lg:block text-white/80 hover:text-white mr-4 p-2 rounded-lg hover:bg-white/5 transition-colors"
                onClick={toggleSidebar}
                title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
              >
                <Bars3Icon className={`h-5 w-5 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-0' : 'rotate-180'}`} />
              </button>
              
              <h1 className="text-lg font-semibold text-white mr-3">
                {getCurrentPageTitle()}
              </h1>
              
              {/* Refresh button */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200 ${
                  isRefreshing ? 'animate-spin opacity-50 cursor-not-allowed' : ''
                }`}
                title="Actualiser les données"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Right side - User menu */}
            <div className="flex items-center space-x-2">

              {/* User menu */}
              <div className="relative flex items-center space-x-3">
                {/* License warning - Hidden for demo */}
                {/* {isLicenseExpiring() && (
                  <div className="bg-yellow-500/20 border border-yellow-500/30 px-3 py-1 rounded-full">
                    <span className="text-yellow-300 text-xs font-medium">
                      Licence expire dans {getRemainingDays()} jours
                    </span>
                  </div>
                )} */}
                
                {/* User info */}
                <div className="flex items-center space-x-2 text-white">
                  <div className="text-right text-sm">
                    <div className="font-medium">{user?.username || "Admin"}</div>
                    <div className="text-white/70 text-xs">{user?.companyName || "TravelIT"}</div>
                  </div>
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-sm font-medium">
                    {user?.username?.charAt(0)?.toUpperCase() || "A"}
                  </div>
                </div>
                
                {/* Logout button */}
                <button 
                  onClick={() => {
                    console.log('🚪 TravelIT: Bouton déconnexion cliqué');
                    
                    // Simply logout - RouteGuard will handle navigation
                    logout();
                    
                    console.log('✅ TravelIT: Déconnexion effectuée - RouteGuard gère la navigation');
                  }}
                  className="flex items-center text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Se déconnecter"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="flex-1 overflow-y-auto p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full"
            >
              <OfflineNotification />
              <Outlet />
            </motion.div>
          </main>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMobileSidebar}
              className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            />
            <motion.div
              initial={{ x: isRTL ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: isRTL ? '100%' : '-100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed inset-y-0 left-0 z-30 w-56 lg:hidden"
            >
              <aside className="h-full flex flex-col bg-white/5 backdrop-blur-xl border-r border-white/10">
                <div className="flex items-center justify-between px-4 py-5">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-xl overflow-hidden mr-3 bg-white/10">
                      <img 
                        src={logoImage} 
                        alt="TravelIT Logo" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h1 className="text-lg font-semibold text-white">
                        {settings.appName || 'TravelIT'}
                      </h1>
                      <p className="text-xs text-white/60">Gestion d'agence de voyage</p>
                    </div>
                  </div>
                  <button
                    onClick={toggleMobileSidebar}
                    className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <nav className="mt-8 flex-1 px-2 overflow-y-auto scrollbar-hide">
                  <div className="space-y-1">
                    {menuItems.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(item.href);
                          closeSidebar();
                        }}
                        className={`group flex items-center ${
                          isRTL ? 'pr-2 pl-3' : 'pl-2 pr-3'
                        } py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          item.current
                            ? 'bg-white/10 text-white'
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <item.icon
                          className={`flex-shrink-0 h-6 w-6 ${
                            isRTL ? 'ml-3' : 'mr-3'
                          }`}
                          aria-hidden="true"
                        />
                        {item.name}
                      </a>
                    ))}
                  </div>
                </nav>
              </aside>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout; 